import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useCMSPageField } from '@/cms/fields'
import { Custom500 } from '@/pages/500'
import { Basket } from '@/scopes/checkout/components/Basket'
import { CheckoutBenefits } from '@/scopes/checkout/components/CheckoutBenefits/CheckoutBenefits'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutFooter } from '@/scopes/checkout/components/CheckoutFooter'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import {
  CheckoutHeading,
  CheckoutHeadingDescription,
  CheckoutHeadingTitle,
} from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep/CheckoutNextStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { CheckoutUsps } from '@/scopes/checkout/components/CheckoutUsps/CheckoutUsps'
import { Content, Loader, MainContent, PageLayout, PrimaryContent, SideBarContent } from '@/scopes/checkout/components/Layout'
import { Services } from '@/scopes/checkout/components/services'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { useSendImpressionViewAncillaries } from '@/scopes/checkout/domain/analytics/useSendImpressionViewAncillaries'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { CheckoutOptimizelyFlagsProvider } from '@/scopes/checkout/domain/CheckoutOptimizelyFlagsProvider'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import styles from '@/scopes/checkout/pages/Pages.module.css'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'

const queryClient = new QueryClient()

function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const intl = useIntl()
  const brandName = useConfiguration('brandName')
  useSendCheckoutEvent('checkout_1_reached')
  useSendImpressionViewAncillaries()

  const methods = useForm({ mode: 'onChange' })
  const promotionTextSecondLine = intl.messages.checkout_steps_header_my_portal_promotion_text_line_2
  const FF_ENABLE_SEND_EVENT_NEXTBUTTON_STEP1 = useFeatureFlag('KOS_ENABLE_SEND_EVENT_NEXTBUTTON_STEP1')
  const isNewBasketEnabled = useFeatureFlag('MALLORCA_ENABLE_NEW_BASKET')

  return (
    <PageLayout>
      {data?.error && data.error.hasError && <CheckoutError />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
          <CheckoutHeader />
          <MainContent>
            {isLoading
              ? (
                  <Loader />
                )
              : (
                  <>
                    <CheckoutProgress />
                    <CheckoutHeading>
                      <CheckoutHeadingTitle>
                        {intl.formatMessage({ id: 'checkout_steps_header_step_1_title' })}
                      </CheckoutHeadingTitle>
                      <CheckoutHeadingDescription>
                        {intl.formatMessage({ id: 'checkout_steps_header_my_portal_promotion_text_line_1' })}
                        {promotionTextSecondLine && (
                          <>
                            <br />
                            {intl.formatMessage({ id: 'checkout_steps_header_my_portal_promotion_text_line_2' })}
                          </>
                        )}
                      </CheckoutHeadingDescription>
                    </CheckoutHeading>
                    <Content>
                      <PrimaryContent>
                        <FormProvider {...methods}>
                          <div className={styles.wrapper}>
                            <Services />
                          </div>
                          <CheckoutNextStep sendSubmitEvent notifyNextStep={FF_ENABLE_SEND_EVENT_NEXTBUTTON_STEP1} />
                          <CheckoutUsps />
                        </FormProvider>
                      </PrimaryContent>
                      <SideBarContent>
                        <Basket />
                        {!isNewBasketEnabled && <CheckoutBenefits />}
                      </SideBarContent>
                    </Content>
                  </>
                )}
          </MainContent>
          {!isLoading && <CheckoutFooter />}
        </NewRelicErrorBoundary>
      )}
    </PageLayout>
  )
}

export default function CheckoutPageStep1() {
  const contextItemId = useCMSPageField('contextItemId')
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    step: 1,
  }), [contextItemId])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <CheckoutOptimizelyFlagsProvider>
          <MetaTitle titleTranslationTag="checkout_steps_step_1_meta_title" />
          <Page />
          <DevTools />
        </CheckoutOptimizelyFlagsProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
